import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { voiceKeys } from '@/lib/queryKeys';
import { useAuthStore } from '@/stores/auth.store';
import {
  fetchDoNotDisturb,
  getOrCreateDeviceId,
  isCallingConfigured,
  saveDoNotDisturb,
  type DoNotDisturbDevice,
} from '@/lib/voip/callingService';
import { useRegistrationState } from '@/stores/voip.store';
import {
  describeDoNotDisturbEnd,
  deviceLabel,
  isDoNotDisturbInForce,
} from '@/lib/voip/doNotDisturbDurations';
import { useScheduleTimezone } from '@/lib/time/scheduleTimezone';

/** setTimeout cannot wait longer than a signed 32-bit number of milliseconds. */
const MAX_TIMER_MS = 2 ** 31 - 1;

type Change = { deviceId: string; enabled: boolean; until?: Date | null };

export interface DoNotDisturbDeviceView extends DoNotDisturbDevice {
  /** "Web" / "Dana's iPhone", ready to show. */
  label: string;
  /** True only while the setting is still in force. */
  isOn: boolean;
  /** "until 3:30 PM" while on — a phrase to finish a sentence with. */
  endsLabel: string | null;
  /** The browser this page is running in — the person's web softphone. */
  isThisBrowser: boolean;
}

/**
 * The signed-in user's Do Not Disturb, per device.
 *
 * Someone can silence this browser and leave their phone ringing, or silence
 * the phone from here. Each device carries its own end time. The switch changes
 * straight away and rolls back if the save fails, with a toast saying so — a
 * switch that shows "on" while calls still ring is worse than one that visibly
 * refuses.
 *
 * Read once per page load and never on focus or retry. The header mounts this
 * for everyone on every page, and the calling service rate-limits per IP
 * (100 requests / 15 min) across ALL its routes — so an office of dispatchers
 * refetching on every tab switch could starve the SIP login the softphone
 * needs. The server decides the ringing either way; this is only the switch.
 *
 * A timed DND ends on the server by itself. Here a timer re-renders when the
 * soonest one lapses, so the badge clears on time without asking again.
 */
export function useDoNotDisturb() {
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.userId);
  // Keyed by the user so a switch of person inside the same tenant (an
  // impersonation handoff, a re-login) can never show one person's DND on
  // another's session — the cache never goes stale on its own.
  const queryKey = voiceKeys.doNotDisturb(userId ?? null);
  const enabled = isCallingConfigured && !!token && !!userId;
  // End times are shown, and "tomorrow at 8" is worked out, in the same zone as
  // every other time in the app.
  const { displayTimezone } = useScheduleTimezone();
  const registration = useRegistrationState();

  const query = useQuery({
    queryKey,
    queryFn: fetchDoNotDisturb,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    // NOT-747 — this read must never raise a page-level alert. Nobody asks for
    // it: the header mounts it on every page, and it goes to the calling
    // service, a separate host from operations. When that host is unreachable
    // from the browser the fetch rejects with a bare `TypeError: Failed to
    // fetch`, which the shared handler turned into a "Failed to fetch" toast on
    // whatever page happened to be open — a job, an invoice being settled, a
    // visit being rescheduled — with nothing on that page actually broken. The
    // only honest consequence is the switch staying hidden, which the
    // `isAvailable` check below already does. The failure still reaches Sentry
    // and RUM: `handleQueryError` reports before it consults this.
    meta: { suppressToast: true },
  });

  const [now, setNow] = useState(() => Date.now());
  const devices = useMemo<DoNotDisturbDeviceView[]>(() => {
    const list = query.data ?? [];
    // Which row IS this browser. The calling service vends one shared 'web'
    // credential per 2.0 user, so normally there is exactly one web device — but
    // never guess: prefer an exact match on the id this browser registered with,
    // and when several web devices somehow exist and none matches, claim none of
    // them rather than silencing the wrong one. They all still appear in the list.
    const myDeviceId = getOrCreateDeviceId();
    const webDevices = list.filter((device) => device.platform === 'web');
    const exact = webDevices.find((device) => device.deviceId === myDeviceId);
    const mine = exact?.deviceId ?? (webDevices.length === 1 ? webDevices[0].deviceId : null);

    return list.map((device) => {
      const isOn = isDoNotDisturbInForce(device, now);
      return {
        ...device,
        label: deviceLabel(device),
        isOn,
        endsLabel: isOn
          ? describeDoNotDisturbEnd(device.until, displayTimezone, new Date(now))
          : null,
        isThisBrowser: mine !== null && device.deviceId === mine,
      };
    });
  }, [query.data, now, displayTimezone]);

  // The soonest end time still ahead: one timer covers every device.
  const nextEnd = devices
    .map((device) => (device.isOn && device.until ? Date.parse(device.until) : NaN))
    .filter((at) => !Number.isNaN(at) && at > now)
    .sort((a, b) => a - b)[0];
  useEffect(() => {
    if (!nextEnd) return;
    const wait = nextEnd - Date.now();
    if (wait <= 0) return;
    // A wait longer than a timer allows is chained: each tick re-renders,
    // re-checks and schedules the next, so it never ends early.
    const timer = setTimeout(() => setNow(Date.now()), Math.min(wait + 50, MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [nextEnd]);

  // A brand-new browser can ask for its devices before the SIP login has created
  // the web one, and this query never goes stale on its own — so the menu would
  // stay hidden for the rest of the session. One refetch once the softphone is
  // registered, and only while the list is empty, fixes that without adding a
  // poll to a rate-limited service.
  const registered = registration === 'registered';
  const noDevices = query.data?.length === 0;
  useEffect(() => {
    if (!enabled || !registered || !noDevices) return;
    void queryClient.invalidateQueries({ queryKey });
    // queryKey is derived from userId, which is in `enabled`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, registered, noDevices]);

  const mutation = useMutation({
    mutationFn: ({ deviceId, enabled: on, until }: Change) =>
      saveDoNotDisturb(deviceId, on, on ? (until ?? null) : null),
    onMutate: async (change: Change) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DoNotDisturbDevice[]>(queryKey);
      queryClient.setQueryData<DoNotDisturbDevice[]>(queryKey, (current) =>
        (current ?? []).map((device) =>
          device.deviceId === change.deviceId
            ? {
                ...device,
                enabled: change.enabled,
                until: change.enabled && change.until ? change.until.toISOString() : null,
              }
            : device,
        ),
      );
      setNow(Date.now());
      return { previous };
    },
    onError: (_error, _change, context) => {
      queryClient.setQueryData<DoNotDisturbDevice[]>(queryKey, context?.previous ?? []);
      toast.error("Couldn't change Do Not Disturb. Please try again.");
    },
    onSuccess: (saved) => {
      if (!saved) return;
      queryClient.setQueryData<DoNotDisturbDevice[]>(queryKey, (current) =>
        (current ?? []).map((device) =>
          device.deviceId === saved.deviceId ? { ...device, ...saved } : device,
        ),
      );
      setNow(Date.now());
      const where = deviceLabel(saved);
      toast.success(
        saved.enabled
          ? `${where} is silenced ${describeDoNotDisturbEnd(saved.until, displayTimezone)}`
          : `${where} will ring again`,
      );
    },
  });

  const thisBrowser = devices.find((device) => device.isThisBrowser) ?? null;

  return {
    /** Every device this person can be rung on, with its own state. */
    devices,
    /** The web softphone this page is running, if they have one. */
    thisBrowser,
    /** This browser is silenced — what the header badge reflects. */
    isOn: thisBrowser?.isOn ?? false,
    endsLabel: thisBrowser?.endsLabel ?? null,
    /** How many of their devices are silenced right now. */
    silencedCount: devices.filter((device) => device.isOn).length,
    /**
     * Hidden until the devices have loaded, so it never shows a guess — and
     * stays hidden when calling is off for this build, when the calling service
     * predates DND, or when this person has no device to silence.
     */
    isAvailable: enabled && query.data !== undefined && devices.length > 0,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    /** The zone durations like "tomorrow at 8" are chosen in. */
    timeZone: displayTimezone,
    /** Silence one device until `untilAt`, or until turned off when it is null. */
    turnOn: (deviceId: string, untilAt: Date | null) =>
      mutation.mutate({ deviceId, enabled: true, until: untilAt }),
    /** Let one device ring again. */
    turnOff: (deviceId: string) => mutation.mutate({ deviceId, enabled: false }),
  };
}
